import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useRef, useState, useLayoutEffect } from 'react';
import { getColumnWidthSize, getPercentageValue } from './utils.js';
import '@elliemae/ds-utilities/utils';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function useColumnSizeService(instance) {
  var columns = useRef({});

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      expandableGridDetailWidth = _useState2[0],
      setExpandableGridDetailWidth = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      expandableColumnWidth = _useState4[0],
      setExpandableColumnWidth = _useState4[1];

  var expandedToContentColumns = useRef({});
  var previousTotalWidth = useRef(0);
  var initialUsedSpace = useRef({
    space: 0,
    amount: 0,
    percentage: 0
  });

  var increaseUsedSpace = function increaseUsedSpace(space, columnUuid) {
    if (typeof space === 'number') {
      initialUsedSpace.current.space += space;
    } else if (space && space.endsWith('%')) {
      initialUsedSpace.current.percentage += Number(space.slice(0, -1));
    }

    initialUsedSpace.current.amount += 1;
    initialUsedSpace.current[columnUuid] = true;
  }; // eslint-disable-next-line no-return-assign


  var registerColumn = function registerColumn(column) {
    return function (node) {
      return columns.current[column.uuid] = _objectSpread(_objectSpread({}, column), {}, {
        node: node
      });
    };
  };

  var unregisterColumn = function unregisterColumn(column) {
    delete columns.current[column.property];
  };

  var setActualColumnsSizesFixed = function setActualColumnsSizesFixed() {
    var columnsArray = Object.values(columns.current);
    columnsArray.forEach(function (column) {
      var columnOffsetWidth = column.node.offsetWidth;
      column.updateBodyHeaderWidth("".concat(columnOffsetWidth, "px"), "".concat(columnOffsetWidth, "px"));
    });
  };

  var updateMissingWidths = function updateMissingWidths(totalWidth) {
    if (totalWidth) {
      previousTotalWidth.current = totalWidth;
    }

    var gridWidth = previousTotalWidth.current - 4;

    var _instance$getInstance = instance.getInstance(),
        _instance$getInstance2 = _instance$getInstance.props,
        minColumnWidth = _instance$getInstance2.minColumnWidth,
        expandable = _instance$getInstance2.expandable,
        resizedColumns = _instance$getInstance.resizedColumns,
        columnDefinitions = _instance$getInstance.decoratedColumns;

    if (!initialUsedSpace.current) return;
    var columnUuids = columnDefinitions.map(function (c) {
      return c.uuid;
    });
    var columnsArray = Object.values(columns.current).filter(function (c) {
      return columnUuids.includes(c.uuid);
    }); // reset the initial space to the last columns

    initialUsedSpace.current = {
      space: 0,
      amount: 0,
      percentage: 0
    };
    columnsArray.forEach(function (column) {
      if (column.width && column.width !== 'string' && !initialUsedSpace[column.uuid]) {
        increaseUsedSpace(getColumnWidthSize(column.width, minColumnWidth, column), column.uuid);
      }
    });
    var nextColumnDict = {}; // eslint-disable-next-line max-statements

    columnsArray.forEach(function (column) {
      if (typeof column.width === 'number') {
        nextColumnDict[column.uuid] = column;
        return;
      }

      if (resizedColumns.current[column.uuid]) {
        nextColumnDict[column.uuid] = column;
        return;
      }

      var _initialUsedSpace$cur = initialUsedSpace.current,
          usedSpace = _initialUsedSpace$cur.space,
          usedAmount = _initialUsedSpace$cur.amount,
          usedPercentage = _initialUsedSpace$cur.percentage;
      var percentValue = getPercentageValue(column.width); // check if is percentage value

      if (percentValue) {
        var pxs = gridWidth * percentValue / 100;
        var pxWidths = "".concat(getColumnWidthSize(pxs, minColumnWidth, column), "px");
        column.updateBodyHeaderWidth(pxWidths, pxWidths);
        columns.current[column.uuid].computedWidth = pxs;

        if (expandable && column.expandableColumn) {
          setExpandableColumnWidth(pxs);
        }
      } else {
        // otherwise the values are auto calculated from the total size
        var columnFixWidth = (gridWidth * (1 - usedPercentage / 100) - usedSpace) / (columnsArray.length - usedAmount);
        var percentWidth = columnFixWidth / gridWidth * 100;

        var _pxs = gridWidth * percentWidth / 100;

        var actualWidthAfterChecks = getColumnWidthSize(_pxs, minColumnWidth, column);
        column.updateBodyHeaderWidth("".concat(actualWidthAfterChecks, "px"), column.minWidth || minColumnWidth ? "".concat(column.minWidth || minColumnWidth, "px") : "".concat(_pxs, "px"));
        resizedColumns.current[column.uuid] = actualWidthAfterChecks;
        columns.current[column.uuid].computedWidth = actualWidthAfterChecks;
      }

      nextColumnDict[column.uuid] = column;
    });
    columns.current = nextColumnDict;
  };

  useLayoutEffect(function () {
    var totalWidth = computeColumnsTotalSize();
    setExpandableGridDetailWidth(totalWidth - expandableColumnWidth);
  }, [expandableColumnWidth]); // todo: make this incremental

  var computeColumnsTotalSize = function computeColumnsTotalSize() {
    var columnsArray = Object.values(columns.current);
    return columnsArray.reduce(function (totalSize, column) {
      return totalSize + column.node.offsetWidth;
    }, 0);
  };

  return {
    columns: columns.current,
    expandedToContentColumns: expandedToContentColumns.current,
    initialUsedSpace: initialUsedSpace.current,
    increaseUsedSpace: increaseUsedSpace,
    registerColumn: registerColumn,
    unregisterColumn: unregisterColumn,
    updateMissingWidths: updateMissingWidths,
    computeColumnsTotalSize: computeColumnsTotalSize,
    setActualColumnsSizesFixed: setActualColumnsSizesFixed,
    expandableGridDetailWidth: expandableGridDetailWidth,
    expandableColumnWidth: expandableColumnWidth
  };
}

export default useColumnSizeService;
//# sourceMappingURL=useColumnSizeService.js.map
